import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Heart,
  Target,
  Users,
  TrendingUp,
  GraduationCap,
  Globe,
  CheckCircle,
  ArrowRight,
  Shield,
  Gem,
  Handshake,
  Linkedin,
} from "lucide-react";


const Mission = () => {
  const values = [
    {
      icon: Heart,
      title: "Education First",
      description:
        "We believe knowledge is power. Before you invest a single dollar, you should understand what you're doing.",
    },
    {
      icon: Target,
      title: "No Hype",
      description:
        "No get-rich-quick promises. No meme stocks. Just solid, proven strategies for long-term growth.",
    },
    {
      icon: Users,
      title: "Community",
      description:
        "Learning is better together. Join a community of students who support each other's financial journey.",
    },
    {
      icon: TrendingUp,
      title: "Generational Wealth Building",
      description:
        "We focus on long-term strategies that compound over time, building wealth that lasts generations.",
    },
    {
      icon: Shield,
      title: "Trust & Transparency",
      description:
        "Everything we share is backed by real experience. No hidden agendas, just honest guidance.",
    },
    {
      icon: Gem,
      title: "Integrity",
      description:
        "We only recommend what we believe in and use ourselves. Your success is our success.",
    },
  ];

  const founders = [
    {
      name: "Arsh Dharani",
      role: "Founder",
      image: "https://customer-assets.emergentagent.com/job_finance-tools-33/artifacts/8kx7soer_Screenshot%202024-11-13%20at%2012.23.22%E2%80%AFPM.jpg",
      education: "Financial and Business Economics, York University",
      highSchool: "Aga Khan Academy, Mombasa",
      accolades: [
        "PwC Audit & Assurance",
        "President's International Scholarship ($180,000)",
        "Founder, TutorCraft",
        "Aga Khan Agency Microfinance (Geneva)",
      ],
      companies: [
        { name: "PwC", logo: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/39lr92sq_image.png" },
        { name: "Aga Khan Development Network", logo: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/wygt9lg5_image.png" },
      ],
      linkedin: "https://www.linkedin.com/in/arshdharani786",
      story: `When I first arrived in Canada as an international student, I had no idea where to start with investing. Nobody in my circle talked about TFSAs, RRSPs, or how the Canadian market worked. I felt completely alone trying to figure it all out.

I started small, really small. I made mistakes, learned from them, and slowly built a system that worked around my busy student life. Working at PwC exposed me to how major institutions manage billions, and I realized the same principles could work for students too. My internship as a Financial Analyst at the Aga Khan Agency for Microfinance in Geneva opened my eyes to how finance can create real impact in developing communities.

Over the years, I've built a portfolio that I'm proud of, but more importantly, I've developed the knowledge and habits that will serve me for life. I created Compoundr because I don't want other students to feel as lost as I did. Every tool on this platform is something I wish I had when I started.`,
    },
    {
      name: "Ayan Charnia",
      role: "Co-Founder",
      image: "https://customer-assets.emergentagent.com/job_98dde3fb-acc5-4535-bdb3-3bc49032bb4d/artifacts/iv5yhc8i_Ayan%20Picture%20%281%29.png",
      education: "Economics (Finance Option), University of Victoria",
      highSchool: "Aga Khan Academy, Mombasa",
      accolades: [
        "Portfolio Manager, $1.9M Student Investment Fund",
        "Dixon Mitchell Investment Counsel",
        "Acumen Capital Partners",
        "Aga Khan Academy Scholar ($90,000)",
      ],
      companies: [
        { name: "Dixon Mitchell Investment Counsel", logo: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/k6vtq6ku_image.png" },
        { name: "Acumen Capital Partners", logo: "https://customer-assets.emergentagent.com/job_fintoolkit/artifacts/okb8i2dl_Screenshot%202026-02-07%20at%2019.53.28.png" },
      ],
      linkedin: "https://www.linkedin.com/in/ayancharnia/",
      story: `My journey with investing began with a simple question: "How do wealthy people actually build wealth?" As an international student from the DRC, a developing country, I was determined to find out.

I dove deep into finance, not just theory, but real practice. Being selected as one of 5 students to manage a $1.9 million portfolio at the University of Victoria changed everything. I learned what it truly means to be responsible for other people's money and how to make decisions under pressure.

Working with Dixon Mitchell and Acumen Capital Partners showed me the institutional side of investing, but I never forgot where I started, a student with limited funds trying to build something meaningful. That's why Compoundr matters to me. We're building the tools and education I wish existed when I was just starting out.`,
    },
  ];

  return (
    <div className="min-h-screen" data-testid="mission-page">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 gradient-mesh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-6 text-sm font-medium">
            ← Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">
              Our Mission
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-navy-700 mb-4 leading-tight">
              Empowering Students to Build
              <span className="text-emerald-500"> Financial Freedom</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Compoundr was built by international students who learned investing the hard way. 
              We created the platform we wish existed when we started: clear education, practical tools, 
              and guidance that actually makes sense for students in Canada.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-700 mb-3">
              Why We Built Compoundr
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We were international students who felt lost when it came to investing in Canada. 
              No one explained TFSAs, RRSPs, or how to actually get started. We had to figure it all out ourselves.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">Started From Zero</h3>
              <p className="text-slate-600">
                As international students, we arrived in Canada with no financial knowledge and no guidance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">Built Strong Portfolios</h3>
              <p className="text-slate-600">
                In under 5 years, we went from complete beginners to managing our own successful portfolios.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Handshake className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">Giving Back</h3>
              <p className="text-slate-600">
                Now we're sharing everything we learned (the tools, strategies, and lessons) so you don't have to struggle alone.
              </p>
            </motion.div>
          </div>

          <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="p-8 text-center">
              <p className="text-xl text-emerald-800 font-medium italic">
                "We built every tool on Compoundr because we personally needed it. The calculators, 
                the guides, the ETF quiz... these are tools that helped us on our own journey."
              </p>
              <p className="text-emerald-600 mt-4 font-semibold">Arsh & Ayan</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Meet the Founders */}
      <section className="py-12 lg:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-navy-700 text-white mb-3">Leadership</Badge>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-700 mb-2">
              Meet the Founders
            </h2>
            <p className="text-lg text-slate-600">
              Built by students, for students
            </p>
          </div>

          <div className="space-y-12">
            {founders.map((founder, index) => (
              <motion.div
                key={founder.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="grid lg:grid-cols-[280px_1fr] gap-8 items-start"
              >
                {/* Photo and Info Card - Always on Left */}
                <div>
                  <Card className="overflow-hidden border-0 shadow-floating">
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={founder.image}
                        alt={founder.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-heading font-bold text-navy-700">
                            {founder.name}
                          </h3>
                          <Badge className="bg-emerald-100 text-emerald-700 mt-1">
                            {founder.role}
                          </Badge>
                        </div>
                        <a
                          href={founder.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center transition-colors"
                        >
                          <Linkedin className="w-5 h-5 text-white" />
                        </a>
                      </div>

                      <div className="space-y-2 mb-4">
                        <p className="text-slate-600 flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-emerald-600" />
                          {founder.education}
                        </p>
                        {founder.highSchool && (
                          <p className="text-slate-500 text-sm flex items-center gap-2">
                            <GraduationCap className="w-3 h-3 text-slate-400" />
                            {founder.highSchool}
                          </p>
                        )}
                      </div>

                      {/* Company Logos */}
                      {founder.companies && founder.companies.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-navy-700 uppercase tracking-wide mb-3">
                            Experience At
                          </p>
                          <div className="flex flex-wrap gap-4 items-center">
                            {founder.companies.map((company, i) => (
                              <div
                                key={i}
                                className="h-20 px-6 bg-white border border-slate-200 rounded-xl flex items-center justify-center shadow-sm"
                                title={company.name}
                              >
                                <img
                                  src={company.logo}
                                  alt={company.name}
                                  className="h-14 max-w-[180px] object-contain"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <span className="text-sm font-semibold text-slate-700 hidden items-center">{company.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Accolades */}
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-navy-700 uppercase tracking-wide">
                          Credentials
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {founder.accolades.map((accolade, i) => (
                            <Badge
                              key={i}
                              variant="outline"
                              className="text-xs border-slate-300 text-slate-600"
                            >
                              {accolade}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Story - Always on Right */}
                <div>
                  <div className="lg:sticky lg:top-8">
                    <h4 className="text-sm font-semibold text-emerald-600 uppercase tracking-wide mb-2">
                      {founder.role === "Founder" ? "The Vision" : "The Mission"}
                    </h4>
                    <h3 className="text-2xl font-heading font-bold text-navy-700 mb-6">
                      {founder.name}'s Story
                    </h3>
                    <div className="prose prose-slate max-w-none">
                      {founder.story.split("\n\n").map((paragraph, i) => (
                        <p key={i} className="text-slate-600 leading-relaxed mb-4">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-10 lg:py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-700 mb-2">
              What Makes Compoundr Different
            </h2>
            <p className="text-lg text-slate-600">
              Built from real experience, not textbooks
            </p>
          </div>

          <div className="space-y-3">
            {[
              "We've personally used every strategy and tool we teach",
              "We show you how to find legitimate beginner bonuses from Canadian platforms",
              "We help you create a budget that actually works for student life",
              "We build a monthly investing system you can set and forget",
              "We explain Canadian accounts (TFSA, RRSP, FHSA) in plain English",
              "We never promise returns or sell you on risky strategies",
              "We've managed real money, from personal portfolios to 7-figure institutional funds",
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl"
              >
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-10 lg:py-14 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-700 mb-2">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full card-hover border-0 shadow-card text-center">
                  <CardContent className="pt-8 pb-8">
                    <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <value.icon className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-navy-700 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-slate-600">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h4 className="font-heading font-bold text-navy-700 mb-2">
                Important Disclaimer
              </h4>
              <p className="text-sm text-slate-600">
                Compoundr is educational and does not provide personalized
                financial advice. The content on this website is for informational
                purposes only. Always do your own research and consider speaking
                with a licensed financial professional for advice specific to your
                situation. Investing involves risk, including the potential loss of
                principal.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-br from-navy-700 to-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Globe className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-3">
            Ready to Start Your Journey?
          </h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            Join thousands of students who are building their financial future. 
            Start with our free tools and resources. No credit card required.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-4 text-base">
                Explore Free Tools
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="text-white border-white hover:bg-white/10 rounded-full px-6 py-4 text-base">
                View Resources
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Join Our Team */}
      <section className="py-10 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Users className="w-6 h-6 text-emerald-500" />
            <h2 className="text-xl sm:text-2xl font-heading font-bold text-navy-700">
              Join Our Team
            </h2>
          </div>
          <p className="text-slate-600 mb-4 max-w-2xl mx-auto text-sm">
            Passionate about helping students build financial literacy? We&apos;re always looking for talented individuals who share our mission. Send us your resume and let&apos;s build something great together.
          </p>
          <a
            href="mailto:compoundrca@gmail.com"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-full transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="20" height="16" x="2" y="4" rx="2"/>
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
            </svg>
            compoundrca@gmail.com
          </a>
        </div>
      </section>

      
    </div>
  );
};

export default Mission;
